import {useEffect, useRef, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {CheckCircle2, TriangleAlert, X} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  NativeSelect,
  NativeSelectOption,
  SheetClose,
  SheetDescription,
  SheetTitle,
  Spinner
} from '@kroll-resolver-crisp/ui-components-react';
import {
  useModerationLanguages,
  useModerationPolicyViolations,
  useModerationQueueSettings,
  useModerationSettings,
  useServeNextQueueItem,
  useHeartbeatQueueItem,
  useReleaseQueueItem,
  useDismissQueueItem,
  useIndeterminateQueueItem,
  useClassificationConfig,
  useLanguages,
  useTopics,
  useLocations
} from '../api/hooks';
import {
  isLeaseLost,
  resolvedElsewhereOf,
  type ResolvedElsewhere,
  type ModerationLease,
  type ModerationQueueKind,
  type ModerationTask,
  type ReviewSignals
} from '../api/moderationQueue';
import {type Item, itemsApi} from '../api/items';
import {ClassifyDialog} from '../components/investigations/ClassifyDialog';
import {ItemSheet} from '../components/items/ItemDetailPanel';
import {QueueBatchView} from '../components/queues/QueueBatchView';
import {QueueDecisionPanel} from '../components/queues/QueueDecisionPanel';
import {QueueGroupsSection, type GroupDraft} from '../components/queues/QueueGroupsSection';
import {QueueClassificationPath} from '../components/queues/QueueClassificationPath';
import {QueueFilterBar} from '../components/queues/QueueFilterBar';
import {QueueSidebar} from '../components/queues/QueueSidebar';
import {
  MAX_EVIDENCE_VIDEOS,
  QueueFlagReason,
  QueueItemPanel
} from '../components/queues/QueueItemPanel';
import {QueueLeaseIndicator} from '../components/queues/QueueLeaseIndicator';
import {QueueStartCard} from '../components/queues/QueueStartCard';
import {toItem} from '../lib/queueItem';
import {
  claimFilters,
  readFilters,
  selectionCounts,
  writeFilters,
  type QueueFilters
} from '../lib/queueFilters';
import {useAuth} from '../context/useAuth';
import {CreateP0AlertDialog, useP0AlertDialog} from '@/components/p0alerts/CreateP0AlertDialog';
import {SensitiveMediaToggle} from '@/components/shared/SensitiveMedia';
import {PageHeaderActions} from '@/components/layout/PageHeader';
import {getApiErrorMessage} from '@/api/client';
import {type FlagColor} from '@/api/flags';
import {showToast} from '@/lib/toast';
import {useSavedViews} from '@/api/savedViewHooks';
import {
  parseQueueGroupQuery,
  queueGroupCount,
  queueGroupSources,
  toClaimSelections,
  toggleSelection,
  type QueueGroup
} from '@/lib/queueGroups';

const SIZE_KEY = 'moderation.queue.size';
const DEFAULT_BATCH_SIZES = [5, 10, 20];

// Module-level so render-time callers (the dialog's getReview) stay free of an impure call.
const secondsSince = (startedAt: number) => (Date.now() - startedAt) / 1000;
const now = () => Date.now();

const storedSize = () => {
  const stored = Number(localStorage.getItem(SIZE_KEY));
  return Number.isInteger(stored) && stored >= 1 ? stored : 1;
};

type Tally = {violative: number; not: number; cant: number; skip: number};
const NO_TALLY: Tally = {violative: 0, not: 0, cant: 0, skip: 0};

export function QueuesPage() {
  const {user} = useAuth();
  const settings = useModerationSettings();
  const [params, setParams] = useSearchParams();
  const filters = readFilters(params);
  const {data: savedViews} = useSavedViews('queue');
  const groups: QueueGroup[] = (savedViews ?? []).map((view) => ({
    id: view.id,
    name: view.name,
    selections: parseQueueGroupQuery(view.query)
  }));
  const activeGroup = groups.find((group) => group.id === filters.group);
  const groupSelections = activeGroup ? toClaimSelections(activeGroup.selections) : undefined;
  const [groupDraft, setGroupDraft] = useState<GroupDraft | null>(null);
  const [queueKind, setQueueKind] = useState<ModerationQueueKind>('video');
  const {data: verticals, isLoading: loadingVerticals} = useModerationPolicyViolations(
    true,
    filters.language ?? undefined,
    queueKind
  );
  const {data: languageCounts} = useModerationLanguages(
    filters.vertical ?? undefined,
    queueKind,
    true
  );
  const {data: queueSettings} = useModerationQueueSettings();
  const serveNext = useServeNextQueueItem();
  const heartbeat = useHeartbeatQueueItem();
  const release = useReleaseQueueItem();
  const dismiss = useDismissQueueItem();
  const indeterminate = useIndeterminateQueueItem();

  // Classification metadata hooks
  const classifications = useClassificationConfig();
  const languages = useLanguages();
  const topics = useTopics();
  const locations = useLocations();

  // The vertical being reviewed. The filters are fixed while a queue is open.
  const [selected, setSelected] = useState<string | null>(null);
  // What the analyst holds: one lease over a single task or a batch. Decided and skipped tasks leave it.
  const [lease, setLease] = useState<ModerationLease | null>(null);
  const [renewedAt, setRenewedAt] = useState(now);
  const [batchTotal, setBatchTotal] = useState(0);
  // Set once a batch's last task is decided, or when its lease is lost with this many tasks undecided.
  const [batchComplete, setBatchComplete] = useState(false);
  const [batchLost, setBatchLost] = useState<number | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [drained, setDrained] = useState(false);
  const [size, setSize] = useState(storedSize);
  const [checked, setChecked] = useState<ReadonlySet<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [evidenceItemIds, setEvidenceItemIds] = useState<ReadonlySet<string>>(new Set());
  const [tally, setTally] = useState(NO_TALLY);
  const batchSizes = settings.data?.batch_sizes ?? DEFAULT_BATCH_SIZES;
  useEffect(() => {
    if (!batchSizes.includes(size)) {
      setSize(1);
      localStorage.setItem(SIZE_KEY, '1');
    }
  }, [batchSizes, size]);
  const tasks = lease?.tasks ?? [];
  const isBatch = batchTotal > 1;
  const current = tasks.find((task) => task.id === openId) ?? null;
  const [classifyItems, setClassifyItems] = useState<Item[]>([]);
  const bulkGroup = useRef<string | undefined>(undefined);
  const p0Dialog = useP0AlertDialog();

  // What the analyst's client reports about how each task was reviewed. Analytics only: it is sent
  // with the decision and never decides whether the decision is accepted.
  const [openedIds, setOpenedIds] = useState<ReadonlySet<string>>(new Set());
  const onScreenSeconds = useRef(new Map<string, number>());
  const shownSince = useRef<number | null>(null);
  const currentId = current?.id;
  useEffect(() => {
    if (!currentId) {
      return;
    }

    const stop = () => {
      if (shownSince.current !== null) {
        const seconds = (Date.now() - shownSince.current) / 1000;
        onScreenSeconds.current.set(
          currentId,
          (onScreenSeconds.current.get(currentId) ?? 0) + seconds
        );
        shownSince.current = null;
      }
    };

    const sync = () => {
      if (document.visibilityState === 'visible') {
        shownSince.current ??= Date.now();
      } else {
        stop();
      }
    };

    sync();
    document.addEventListener('visibilitychange', sync);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', sync);
    };
  }, [currentId]);

  const markOpened = (taskId: string) => {
    setOpenedIds((previous) => new Set(previous).add(taskId));
  };

  const reviewFor = (taskId: string, bulkGroupId?: string): ReviewSignals => {
    const live =
      shownSince.current !== null && taskId === currentId ? secondsSince(shownSince.current) : 0;
    return {
      opened: openedIds.has(taskId),
      on_screen_seconds: Math.round((onScreenSeconds.current.get(taskId) ?? 0) + live),
      ...(bulkGroupId ? {bulk_group_id: bulkGroupId} : {})
    };
  };

  const count = (key: keyof Tally, by = 1) => {
    setTally((previous) => ({...previous, [key]: previous[key] + by}));
  };

  const busy =
    serveNext.isPending || release.isPending || dismiss.isPending || indeterminate.isPending;

  // Filter verticals based on user permissions
  const allVerticals = verticals ?? [];
  const allowedVerticals = user?.allowedVerticals;
  const isBroadView = user?.role === 'admin' || user?.role === 'analyst_admin';

  // Strict role check: Admin/Analyst Admin sees all. Standard analyst MUST match allowedVerticals.
  const summaries = isBroadView
    ? allVerticals
    : allVerticals.filter((v) => allowedVerticals?.includes(v.policy_violation));
  const channelReviewEnabled = queueSettings?.channel_review_enabled ?? false;
  const matching = activeGroup
    ? queueGroupCount(summaries, activeGroup.selections)
    : selectionCounts(summaries, filters);
  const selectedSummary = summaries.find((v) => v.policy_violation === filters.vertical);
  const noAccess = !isBroadView && (!allowedVerticals || allowedVerticals.length === 0);
  const claimSize = queueKind === 'channel' ? 1 : size;
  // A claim is one vertical (the filter's, else the one already under review) or a queue group.
  const claimVertical = filters.vertical ?? selected;

  const changeFilters = (next: QueueFilters) => {
    setParams((previous) => writeFilters(previous, next), {replace: true});
  };

  const leaseOf = (task: ModerationTask) => ({id: task.id, leaseId: task.lease_id});

  const take = (next: ModerationLease | null) => {
    setLease(next);
    setRenewedAt(now());
    setBatchTotal(next?.tasks.length ?? 0);
    setBatchComplete(false);
    setBatchLost(null);
    setDrained(next === null);
    // A single task opens straight away; a batch starts on its grid.
    setOpenId(next?.tasks.length === 1 ? next.tasks[0]!.id : null);
    setChecked(new Set());
    const first = next?.tasks.length === 1 ? next.tasks[0]! : null;
    setEvidenceItemIds(
      new Set(
        first?.content_type === 'actor'
          ? first.channel_videos
              .filter((video) => video.is_strike)
              .slice(0, MAX_EVIDENCE_VIDEOS)
              .map((video) => video.external_id)
          : []
      )
    );
  };

  const changeSize = (next: number) => {
    setSize(next);
    localStorage.setItem(SIZE_KEY, String(next));
  };

  // Claims under the filters or the group. A group lease is one vertical, the one its tasks are in, which
  // becomes the vertical under review; with nothing served the group's name stands in.
  const serve = async (restart: boolean) => {
    const next = await serveNext.mutateAsync({
      ...(groupSelections ? {selections: groupSelections} : {policyViolation: claimVertical!}),
      size: claimSize,
      kind: queueKind,
      filters: claimFilters(filters),
      ...(restart ? {restart} : {})
    });
    take(next);
    if (activeGroup) {
      setSelected(next?.tasks[0]?.policy_violation ?? activeGroup.name);
    }
  };

  const enter = async (label: string) => {
    setSelected(label);
    setDrained(false);

    try {
      await serve(true);
    } catch (error) {
      showToast(getApiErrorMessage(error, 'Failed to open queue.'), 'error');
      setSelected(null);
    }
  };

  // The lease has no tasks left (decided, skipped, or lost), so a failure here drops back to the
  // overview rather than leaving a task on screen to act on (or claiming a false "Queue Cleared").
  const advance = async () => {
    if (!claimVertical && !activeGroup) {
      return;
    }

    setSelected(claimVertical ?? activeGroup!.name);
    try {
      await serve(false);
    } catch (error) {
      showToast(getApiErrorMessage(error, 'Failed to load the next task.'), 'error');
      setLease(null);
      setSelected(null);
    }
  };

  // These tasks are finished with: leave them out of the lease. A single task's lease is then
  // replaced at once; a batch waits on "Batch complete" for the analyst to claim the next one.
  const finish = async (ids: readonly string[]) => {
    if (tasks.every((task) => ids.includes(task.id))) {
      if (isBatch) {
        setLease(null);
        setOpenId(null);
        setChecked(new Set());
        setBatchComplete(true);
      } else {
        await advance();
      }

      return;
    }

    setLease((previous) =>
      previous
        ? {...previous, tasks: previous.tasks.filter((task) => !ids.includes(task.id))}
        : previous
    );
    setChecked((previous) => new Set([...previous].filter((id) => !ids.includes(id))));
    setOpenId(null);
  };

  // Heartbeat, release, dismiss, can’t review and classify all answer 409 once the lease is gone, and
  // it covers every task in it. A batch says so and waits for the analyst; a single task drops and a
  // fresh lease is taken.
  const handleLeaseLost = async (undecided = tasks.length) => {
    setLease(null);
    setOpenId(null);
    if (isBatch) {
      setChecked(new Set());
      setBatchLost(undecided);
      return;
    }

    showToast('This task was reassigned or expired. Loading the next one.', 'error');
    await advance();
  };

  // Someone else classified these tasks' items while they were held: they are done, so they leave the
  // lease without counting as the analyst's decisions, and the analyst is told who did it.
  const handleResolvedElsewhere = async (
    resolved: readonly ResolvedElsewhere[],
    decided: readonly string[] = []
  ) => {
    const gone = resolved.filter((entry) => tasks.some((task) => task.id === entry.task_id));
    if (gone.length > 0) {
      const names = [...new Set(gone.map((entry) => entry.by_name))].join(', ');
      showToast(
        gone.length === 1
          ? `${names} classified this item, so it was removed from your queue.`
          : `${names} classified ${gone.length} of your items, so they were removed from your queue.`,
        'error'
      );
    }

    const leaving = [...decided, ...gone.map((entry) => entry.task_id)];
    if (leaving.length > 0) {
      await finish(leaving);
    }
  };

  // Heartbeat while a lease is held and the page is visible; hidden pages stop beating and send one
  // as soon as they are visible again. One beat keeps every task in a batch.
  const beat = async () => {
    if (!lease) {
      return;
    }

    try {
      const renewed = await heartbeat.mutateAsync(lease.lease_id);
      setRenewedAt(now());
      await handleResolvedElsewhere(renewed.withdrawn ?? []);
    } catch (error) {
      if (isLeaseLost(error)) {
        await handleLeaseLost();
      }
    }
  };

  // Read through a ref so the timer below is keyed on the lease alone, not re-created every render.
  const latestBeat = useRef(beat);
  useEffect(() => {
    latestBeat.current = beat;
  });

  const leaseId = lease?.lease_id;
  const heartbeatSeconds = lease?.heartbeat_seconds;
  useEffect(() => {
    if (!leaseId || !heartbeatSeconds) {
      return;
    }

    let timer: ReturnType<typeof setInterval> | undefined;
    const stop = () => {
      clearInterval(timer);
      timer = undefined;
    };

    const start = () => {
      stop();
      timer = setInterval(() => {
        void latestBeat.current();
      }, heartbeatSeconds * 1000);
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void latestBeat.current();
        start();
      } else {
        stop();
      }
    };

    if (document.visibilityState === 'visible') {
      start();
    }

    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [leaseId, heartbeatSeconds]);

  // Skip (release), Not violative (dismiss) and Can't review (indeterminate) share one flow:
  // act on the lease, then move on.
  const leave = async (
    run: (task: ModerationTask) => Promise<unknown>,
    failure: string,
    key: 'skip' | 'not' | 'cant'
  ) => {
    if (!current) {
      return;
    }

    try {
      await run(current);
    } catch (error) {
      const resolved = resolvedElsewhereOf(error);
      if (resolved) {
        await handleResolvedElsewhere([resolved]);
      } else if (isLeaseLost(error)) {
        await handleLeaseLost();
      } else {
        showToast(getApiErrorMessage(error, failure), 'error');
      }

      return;
    }

    count(key);
    await finish([current.id]);
  };

  // One decision per task, sharing a group id when it is a bulk action so analytics can tell it from
  // single ones.
  const actOnTasks = async (
    picked: readonly ModerationTask[],
    run: (task: ModerationTask, group: string | undefined) => Promise<unknown>,
    {verb, key, bulk}: {verb: string; key: 'skip' | 'not' | 'cant'; bulk: boolean}
  ) => {
    if (picked.length === 0) {
      return;
    }

    const group = bulk ? crypto.randomUUID() : undefined;
    const done: string[] = [];
    const failed: string[] = [];
    const taken: ResolvedElsewhere[] = [];
    let leaseLost = false;
    setBulkBusy(true);
    for (const task of picked) {
      try {
        await run(task, group);
        done.push(task.id);
      } catch (error) {
        const resolved = resolvedElsewhereOf(error);
        if (resolved) {
          taken.push(resolved);
        } else {
          leaseLost ||= isLeaseLost(error);
          failed.push(task.title ?? task.external_id);
        }
      }
    }

    setBulkBusy(false);
    count(key, done.length);
    if (failed.length > 0) {
      showToast(
        `Could not ${verb} ${failed.length} of ${picked.length}: ${failed.join(', ')}`,
        'error'
      );
    }

    if (leaseLost) {
      await handleLeaseLost(tasks.length - done.length - taken.length);
      return;
    }

    await handleResolvedElsewhere(taken, done);
  };

  const actOnChecked = async (
    run: (task: ModerationTask, group: string | undefined) => Promise<unknown>,
    outcome: {verb: string; key: 'skip' | 'not' | 'cant'}
  ) =>
    actOnTasks(
      tasks.filter((task) => checked.has(task.id)),
      run,
      {...outcome, bulk: true}
    );

  const classify = (picked: readonly ModerationTask[], bulk: boolean) => {
    if (picked.length === 0) {
      return;
    }

    bulkGroup.current = bulk ? crypto.randomUUID() : undefined;
    setClassifyItems(picked.map((task) => toItem(task)));
  };

  // The reviewer's own flag on the queued item, set straight away and kept apart from classifying -
  // the same item flag the investigations views and the submissions grid show.
  const setFlag = (color: FlagColor | null) => {
    if (!current) {
      return;
    }

    const {id, item_id: itemId, flag: prior} = current;
    const patchFlag = (flag: FlagColor | null) => {
      setLease((previous) =>
        previous
          ? {
              ...previous,
              tasks: previous.tasks.map((task) => (task.id === id ? {...task, flag} : task))
            }
          : previous
      );
    };

    patchFlag(color);
    itemsApi.setFlags([itemId], color).catch((error: unknown) => {
      patchFlag(prior);
      showToast(
        getApiErrorMessage(error, color ? 'Failed to flag item.' : 'Failed to clear flag.'),
        'error'
      );
    });
  };

  const exit = async () => {
    await Promise.allSettled(tasks.map(async (task) => release.mutateAsync(leaseOf(task))));
    setSelected(null);
    setLease(null);
    setOpenId(null);
    setDrained(false);
    setBatchComplete(false);
    setBatchLost(null);
  };

  // Leaving the page hands back whatever is still held, as skips, so it is not served again on return.
  const leaving = useRef({tasks, release: release.mutateAsync});
  useEffect(() => {
    leaving.current = {tasks, release: release.mutateAsync};
  });
  // One release at a time: a release deletes the lease only once no other task of it is still leased,
  // which concurrent releases can each fail to see.
  useEffect(
    () => () => {
      const {tasks: held, release: releaseTask} = leaving.current;
      void (async () => {
        for (const task of held) {
          await releaseTask({id: task.id, leaseId: task.lease_id}).catch(() => undefined);
        }
      })();
    },
    []
  );

  // The kind is video or channel; it is fixed while a queue is open.
  const headerActions = (
    <PageHeaderActions>
      {selected ? (
        <>
          {lease ? <QueueLeaseIndicator renewedAt={renewedAt} held={tasks.length} /> : null}
          <Button type="button" variant="ghost" size="sm" onClick={() => void exit()}>
            Stop reviewing
          </Button>
        </>
      ) : null}
      <label className="text-muted-foreground flex items-center gap-2 text-xs font-medium">
        Review
        <NativeSelect
          size="sm"
          value={queueKind}
          disabled={selected !== null}
          onChange={(event) => {
            setQueueKind(event.target.value as ModerationQueueKind);
          }}>
          <NativeSelectOption value="video">Videos</NativeSelectOption>
          <NativeSelectOption
            value="channel"
            disabled={!channelReviewEnabled}
            title={channelReviewEnabled ? undefined : 'Channel review is not enabled yet'}>
            Channels
          </NativeSelectOption>
        </NativeSelect>
      </label>
      <SensitiveMediaToggle />
    </PageHeaderActions>
  );

  const decisionPanel = (task: ModerationTask, inline: boolean) => (
    <QueueDecisionPanel
      key={task.id}
      busy={busy || bulkBusy}
      shortcutsPaused={classifyItems.length > 0}
      inline={inline}
      verdict={isBatch ? undefined : <QueueFlagReason task={task} />}
      onClassify={() => {
        classify([task], false);
      }}
      onDismiss={() =>
        void leave(
          async (held) => dismiss.mutateAsync({...leaseOf(held), review: reviewFor(held.id)}),
          'Failed to dismiss task.',
          'not'
        )
      }
      onIndeterminate={(reason) =>
        void leave(
          async (held) =>
            indeterminate.mutateAsync({...leaseOf(held), reason, review: reviewFor(held.id)}),
          'Failed to close task as can’t review.',
          'cant'
        )
      }
      onSkip={() =>
        void leave(
          async (held) => release.mutateAsync(leaseOf(held)),
          'Failed to skip task.',
          'skip'
        )
      }
    />
  );

  const itemPanel = (task: ModerationTask) => (
    <QueueItemPanel
      key={task.id}
      task={task}
      channelQueue={queueKind === 'channel'}
      withFlagReason={isBatch}
      evidenceItemIds={evidenceItemIds}
      onToggleEvidence={(externalId) => {
        setEvidenceItemIds((previous) => {
          const next = new Set(previous);
          if (!next.delete(externalId) && next.size < MAX_EVIDENCE_VIDEOS) {
            next.add(externalId);
          }

          return next;
        });
      }}
      onOpened={() => {
        markOpened(task.id);
      }}
      onFlag={setFlag}
    />
  );

  const claimNextBatch = (
    <Button type="button" disabled={busy} onClick={() => void advance()}>
      Claim next batch
    </Button>
  );

  let body;
  if (!selected) {
    body = loadingVerticals ? (
      <div className="flex flex-1 items-center justify-center gap-2">
        <Spinner />
        <span className="text-muted-foreground text-xs">Loading queues…</span>
      </div>
    ) : (
      <QueueStartCard
        matching={matching}
        filters={filters}
        group={
          activeGroup
            ? {
                name: activeGroup.name,
                sources: queueGroupSources(summaries, activeGroup.selections)
              }
            : null
        }
        size={claimSize}
        batchSizes={batchSizes}
        sizeLocked={queueKind === 'channel'}
        noAccess={noAccess}
        disabled={busy}
        onSizeChange={changeSize}
        onStart={() => {
          const label = activeGroup?.name ?? filters.vertical;
          if (label) {
            void enter(label);
          }
        }}
      />
    );
  } else if (busy && tasks.length === 0) {
    body = (
      <div className="flex flex-1 items-center justify-center gap-2">
        <Spinner />
        <span className="text-muted-foreground text-xs">Fetching next queue item…</span>
      </div>
    );
  } else if (batchLost !== null) {
    body = (
      <div className="m-auto flex w-full max-w-lg flex-col gap-4 p-6">
        <Alert variant="destructive">
          <TriangleAlert />
          <AlertTitle>Your batch went back to the pool</AlertTitle>
          <AlertDescription>
            The lease expired while the tab was inactive, so all {batchLost} undecided items were
            released. Decisions already made are kept.
          </AlertDescription>
        </Alert>
        {claimNextBatch}
      </div>
    );
  } else if (batchComplete) {
    body = (
      <Empty className="m-auto">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <CheckCircle2 />
          </EmptyMedia>
          <EmptyTitle>Batch complete</EmptyTitle>
          <EmptyDescription>
            {matching > 0
              ? `${matching} more match your filters.`
              : 'Nothing else matches your filters.'}
          </EmptyDescription>
        </EmptyHeader>
        {matching > 0 ? claimNextBatch : null}
      </Empty>
    );
  } else if (drained || tasks.length === 0) {
    body = (
      <Empty className="m-auto">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <CheckCircle2 />
          </EmptyMedia>
          <EmptyTitle>Queue Cleared</EmptyTitle>
          <EmptyDescription>
            All items in <strong className="text-foreground">{selected}</strong> that match your
            filters have been reviewed.
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  } else if (isBatch) {
    body = (
      <div className="flex min-h-0 flex-1">
        <QueueBatchView
          tasks={tasks}
          checked={checked}
          opened={openedIds}
          activeId={openId}
          busy={busy || bulkBusy}
          onToggle={(id) => {
            setChecked((previous) => {
              const next = new Set(previous);
              if (!next.delete(id)) {
                next.add(id);
              }

              return next;
            });
          }}
          onSelectAll={() => {
            setChecked(new Set(tasks.map((task) => task.id)));
          }}
          onClear={() => {
            setChecked(new Set());
          }}
          onOpen={(id) => {
            setOpenId(id);
          }}
          onClassify={(task) => {
            classify([task], false);
          }}
          onDismiss={(task) =>
            void actOnTasks(
              [task],
              async (held, group) =>
                dismiss.mutateAsync({...leaseOf(held), review: reviewFor(held.id, group)}),
              {verb: 'dismiss', key: 'not', bulk: false}
            )
          }
          onIndeterminate={(task, reason) =>
            void actOnTasks(
              [task],
              async (held, group) =>
                indeterminate.mutateAsync({
                  ...leaseOf(held),
                  reason,
                  review: reviewFor(held.id, group)
                }),
              {verb: 'close as can’t review', key: 'cant', bulk: false}
            )
          }
          onSkip={(task) =>
            void actOnTasks([task], async (held) => release.mutateAsync(leaseOf(held)), {
              verb: 'return',
              key: 'skip',
              bulk: false
            })
          }
          onDismissChecked={() =>
            void actOnChecked(
              async (task, group) =>
                dismiss.mutateAsync({...leaseOf(task), review: reviewFor(task.id, group)}),
              {verb: 'dismiss', key: 'not'}
            )
          }
          onIndeterminateChecked={(reason) =>
            void actOnChecked(
              async (task, group) =>
                indeterminate.mutateAsync({
                  ...leaseOf(task),
                  reason,
                  review: reviewFor(task.id, group)
                }),
              {verb: 'close as can’t review', key: 'cant'}
            )
          }
          onSkipChecked={() =>
            void actOnChecked(async (task) => release.mutateAsync(leaseOf(task)), {
              verb: 'return',
              key: 'skip'
            })
          }
          onClassifyChecked={() => {
            classify(
              tasks.filter((task) => checked.has(task.id)),
              true
            );
          }}
        />
        <ItemSheet
          item={current}
          items={tasks}
          onNavigate={(task) => {
            setOpenId(task.id);
          }}
          onClose={() => {
            setOpenId(null);
          }}>
          {(task, pager) => (
            <div className="bg-card text-card-foreground flex h-full min-h-0 flex-col">
              <header className="border-border flex shrink-0 items-center gap-2 border-b py-2 pr-3 pl-5">
                <SheetTitle className="m-0 min-w-0 flex-1 truncate text-sm font-medium">
                  Review item
                </SheetTitle>
                <SheetDescription className="sr-only">
                  The task under review and its decision. Step through the batch with the arrows.
                </SheetDescription>
                {pager}
                <SheetClose
                  render={<Button variant="ghost" size="icon-sm" aria-label="Close item" />}>
                  <X />
                </SheetClose>
              </header>
              <div className="min-h-0 flex-1 overflow-auto">{itemPanel(task)}</div>
              {decisionPanel(task, true)}
            </div>
          )}
        </ItemSheet>
      </div>
    );
  } else if (current) {
    body = (
      <div className="flex min-h-0 flex-1">
        <div className="min-w-0 flex-1 overflow-auto">{itemPanel(current)}</div>
        {decisionPanel(current, false)}
      </div>
    );
  }

  const reviewed = tally.violative + tally.not + tally.cant;
  if (settings.data?.queues_enabled !== true) {
    return (
      <Alert>
        <TriangleAlert />
        <AlertTitle>Queues are coming soon</AlertTitle>
        <AlertDescription>Queue access has not been enabled in this environment.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {headerActions}
      {selected ? null : (
        <QueueFilterBar
          filters={filters}
          languages={languageCounts ?? []}
          onChange={changeFilters}
        />
      )}
      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        {selected ? null : (
          <QueueSidebar
            filters={filters}
            summaries={summaries}
            editing={
              groupDraft
                ? {
                    selections: groupDraft.selections,
                    onToggle(selection) {
                      setGroupDraft({
                        ...groupDraft,
                        selections: toggleSelection(groupDraft.selections, selection)
                      });
                    }
                  }
                : undefined
            }
            groups={
              <QueueGroupsSection
                groups={groups}
                summaries={summaries}
                activeId={activeGroup?.id ?? null}
                draft={groupDraft}
                onApply={(id) => {
                  changeFilters({
                    ...filters,
                    group: id,
                    vertical: null,
                    classification: null,
                    scope: null
                  });
                }}
                onDraft={setGroupDraft}
              />
            }
            onSelect={(selection) => {
              changeFilters({...filters, ...selection, group: null});
            }}
          />
        )}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <QueueClassificationPath
            filters={filters}
            tree={selectedSummary?.tree ?? []}
            {...(selected
              ? {}
              : {
                  onClear() {
                    changeFilters({...filters, classification: null, scope: null});
                  }
                })}
          />

          <div className="flex min-h-0 flex-1 overflow-auto">{body}</div>
        </div>
      </div>

      <div className="border-border bg-card text-muted-foreground flex items-center justify-between gap-3 border-t px-3 py-2 text-xs">
        <span>
          Your session: <strong className="text-foreground">{reviewed}</strong> reviewed ·{' '}
          {tally.violative} violative · {tally.not} not violative · {tally.cant} can’t review ·{' '}
          {tally.skip} skipped
        </span>
        <span>{matching} in pool</span>
      </div>

      {/* Classification Modal */}
      <ClassifyDialog
        items={classifyItems}
        classifications={classifications.data ?? []}
        languages={languages.data ?? []}
        topics={topics.data ?? []}
        locations={locations.data ?? []}
        {...(queueKind === 'channel' ? {evidenceItemIds: [...evidenceItemIds]} : {})}
        moderationTasks={Object.fromEntries(
          tasks
            .filter((task) => classifyItems.some((item) => item.id === task.item_id))
            .map((task) => [
              task.item_id,
              {
                id: task.id,
                leaseId: task.lease_id,
                policyViolation: task.policy_violation,
                suggestedConceptIds: task.suggested_concept_ids
              }
            ])
        )}
        getReview={(itemId) => {
          const task = tasks.find((candidate) => candidate.item_id === itemId);
          return task ? reviewFor(task.id, bulkGroup.current) : undefined;
        }}
        onLeaseLost={() => void handleLeaseLost()}
        onResolvedElsewhere={(resolved) => void handleResolvedElsewhere([resolved])}
        onClose={() => {
          setClassifyItems([]);
        }}
        onSubmitted={(submitted, linkedItems, wantP0) => {
          // Each classification finished its task; there is nothing to mark, only the tasks to drop.
          const done = tasks
            .filter((task) => submitted.some((item) => item.id === task.item_id))
            .map((task) => task.id);
          count('violative', done.length);
          void finish(done);
          if (wantP0) {
            p0Dialog.launch(linkedItems);
          }
        }}
      />

      <CreateP0AlertDialog {...p0Dialog.dialogProps()} />
    </div>
  );
}
